import { Button, Card, Flex, Tag, Typography, theme } from 'antd';
import { LeftOutlined, ReadOutlined } from '@ant-design/icons';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CloudflareLogo from './CloudflareLogo';
import IntegrationPathOption from './IntegrationPathOption';
import { NexusLogo } from '../../integrationWizard/components/steps/IntegrationStep/Nexus';
import { guidesLocation } from '../integrationGuidesRoute';
import CloudflareStepHeader from './CloudflareStepHeader';
import type { CfSubCase } from '../api/CfProbeResponse';
import { useCfStepViewed } from '../useCfStepEvents';
import { useOpenNexusOnTheGuides } from '../useOpenNexusOnTheGuides';

const { Title, Paragraph, Text } = Typography;

const PREREQUISITES = [
  'A Cloudflare account, or 2 minutes to create one',
  'Access to your domain registrar (for nameserver changes)',
  'Rights to create a Cloudflare API token',
];

export type CloudflareRecommendationStepProps = {
  domain?: string;
  subCase?: CfSubCase | null;
  onSetUp?: () => void;
  onGoBack?: () => void;
};

const CloudflareRecommendationStep = ({
  domain = '',
  subCase,
  onSetUp,
  onGoBack,
}: CloudflareRecommendationStepProps) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const openNexusOnTheGuides = useOpenNexusOnTheGuides();
  useCfStepViewed('recommendation', { domain, subCase });

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Analyzing your domain"
        description="We're checking your domain to find the best integration path."
      />

      <Card>
        <Flex gap={token.marginXL} wrap="wrap">
          <Flex vertical gap={token.marginMD} flex={1} style={{ minWidth: token.screenXS }}>
            <CloudflareLogo size={token.sizeXXL} />
            <Title level={4} style={{ marginBottom: 0 }}>
              We recommend continuing with Cloudflare
            </Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Based on the signals we detected, your domain seems suited for an automated
              Cloudflare setup. It should take <Text strong>about 5 minutes</Text> to complete
              the process.
            </Paragraph>
            <Flex vertical gap={token.marginXXS}>
              <Text strong>You&apos;ll need</Text>
              <ol style={{ margin: 0, paddingLeft: token.paddingLG }}>
                {PREREQUISITES.map((prerequisite) => (
                  <li key={prerequisite}>
                    <Text>{prerequisite}</Text>
                  </li>
                ))}
              </ol>
            </Flex>
            <Flex vertical gap={token.marginXXS}>
              <Text strong>New to Cloudflare?</Text>
              <Text>Going live can take up to 48 hours while DNS propagates.</Text>
            </Flex>
          </Flex>

          <Flex vertical gap={token.marginSM} flex={1} style={{ minWidth: token.screenXS }}>
            <IntegrationPathOption
              recommended
              icon={<CloudflareLogo size={token.sizeLG} />}
              title="Set up with Cloudflare"
              tag={<Tag color="green">Recommended</Tag>}
              description="Fastest: we set it up automatically for you."
              onClick={() => onSetUp?.()}
            />
            <IntegrationPathOption
              icon={<NexusLogo size={token.sizeLG} />}
              title="Continue with Nexus"
              description="Let our AI Assistant walk you through it."
              onClick={() =>
                openNexusOnTheGuides({ fromStep: 'recommendation', domain, subject: { domain, subCase } })
              }
            />
            <IntegrationPathOption
              icon={<ReadOutlined style={{ fontSize: token.fontSizeLG, color: token.colorText }} />}
              title="Follow the integration guides"
              description="Step-by-step docs for your stack."
              onClick={() => navigate(guidesLocation(location.search))}
            />
          </Flex>
        </Flex>
      </Card>

      {onGoBack && (
        <Flex>
          <Button type="link" icon={<LeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default CloudflareRecommendationStep;
